import {
  ClassicEditor,
  Alignment,
  Autoformat,
  Bold,
  Essentials,
  GeneralHtmlSupport,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  TextTransformation,
  Underline,
  EditorConfig,
  SourceEditing,
  FontColor,
  Heading,
  Indent,
  IndentBlock,
  PageBreak,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  Input,
  AutoImage,
  Image,
} from 'ckeditor5';
// @ts-ignore
import german from 'ckeditor5/translations/de';
// @ts-ignore
import spanish from 'ckeditor5/translations/es';
// @ts-ignore
import french from 'ckeditor5/translations/fr';
// @ts-ignore
import italian from 'ckeditor5/translations/it';
// @ts-ignore
import dutch from 'ckeditor5/translations/nl';
import 'ckeditor5/ckeditor5.css';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import { AccountFeatureFlag, FeatureFlag, get, getAccountFeatureFlag } from '../FeatureFlagService';
import { getLanguage } from '../PreferencesService/PreferencesService';

import FileManagerPlugin from './FileManagerPlugin';
import SizePlugin from './SizePlugin';
import TableAttributesPlugin from './TableAttributesPlugin';

const TRANSLATIONS = {
  de: german,
  es: spanish,
  fr: french,
  it: italian,
  nl: dutch,
};

ClassicEditor.defaultConfig = {
  toolbar: {
    items: [
      'link',
      '|',
      'bold',
      'italic',
      'underline',
      '|',
      'numberedList',
      'bulletedList',
      '|',
      'alignment:left',
      'alignment:center',
      'alignment:right',
      'alignment:justify',
      '|',
      'sourceEditing',
    ],
    shouldNotGroupWhenFull: false,
  },
  plugins: [
    Alignment,
    Autoformat,
    Bold,
    Essentials,
    GeneralHtmlSupport,
    Italic,
    Link,
    List,
    ListProperties,
    Paragraph,
    PasteFromOffice,
    SizePlugin,
    SourceEditing,
    TextTransformation,
    Underline,
  ],
  link: {
    defaultProtocol: 'https://',
    decorators: {
      toggleDownloadable: {
        mode: 'manual',
        label: 'Downloadable',
        attributes: {
          download: 'file',
        },
      },
      openInNewTab: {
        mode: 'manual',
        label: 'Open in a new tab',
        defaultValue: true,
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
    },
  },
  list: {
    properties: {
      styles: false,
    },
  },
  licenseKey: environment.CK_EDITOR_KEY,
} satisfies EditorConfig;

const advancedConfig = merge({}, ClassicEditor.defaultConfig, {
  toolbar: {
    items: [
      'fileManager',
      'link',
      '|',
      'bold',
      'italic',
      'underline',
      '|',
      'numberedList',
      'bulletedList',
      '|',
      'alignment:left',
      'alignment:center',
      'alignment:right',
      'alignment:justify',
      '|',
      'outdent',
      'indent',
      '|',
      'fontColor',
      '|',
      'heading',
      '|',
      'pageBreak',
      'insertTable',
      '|',
      'sourceEditing',
    ],
  },
  plugins: [
    ...ClassicEditor.defaultConfig.plugins!,
    AutoImage,
    FontColor,
    Heading,
    Indent,
    IndentBlock,
    Input,
    PageBreak,
    Table,
    TableAttributesPlugin,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    FileManagerPlugin,
    Image,
  ],
  heading: {
    options: [
      {
        model: 'paragraph',
        title: 'Paragraph',
        class: 'ck-heading_paragraph',
      },
      {
        model: 'heading1',
        view: 'h1',
        title: 'Heading 1',
        class: 'ck-heading_heading1',
      },
      {
        model: 'heading2',
        view: 'h2',
        title: 'Heading 2',
        class: 'ck-heading_heading2',
      },
      {
        model: 'heading3',
        view: 'h3',
        title: 'Heading 3',
        class: 'ck-heading_heading3',
      },
      {
        model: 'heading4',
        view: 'h4',
        title: 'Heading 4',
        class: 'ck-heading_heading4',
      },
    ],
  },
  htmlSupport: {
    allow: [
      {
        name: /^.*$/,
        styles: true,
        attributes: true,
        classes: true,
      },
    ],
    disallow: [{ name: 'script' }, { name: 'iframe' }],
  },
}) satisfies EditorConfig;

window.ckEditorInstances = window.ckEditorInstances || {};

const configureEditor = (editor: ClassicEditor, events?: Record<string, (props?: any) => void>) => {
  window.ckEditorInstances[(editor.sourceElement as HTMLTextAreaElement).id] = editor;

  let resizeObserver: ResizeObserver;

  const toolbarItems = editor.config.get('toolbar.items');
  if (toolbarItems && toolbarItems.length === 0) {
    editor.keystrokes.destroy();
  }

  for (const event in events) {
    if (event === 'instanceReady') {
      events[event].call(editor);
      continue;
    }

    if (event === 'resize') {
      resizeObserver = new ResizeObserver(() => events[event]({}));
      resizeObserver.observe(editor.ui.view.editable.element!);

      continue;
    }

    editor.on(event, events[event]);
  }

  editor.on('destroy', () => {
    resizeObserver?.disconnect();
    delete window.ckEditorInstances[(editor.sourceElement as HTMLTextAreaElement).id];
  });

  return editor;
};

export const replace = (
  id: string | HTMLElement,
  config?: EditorConfig,
  events?: Record<string, (props?: any) => void>,
) => {
  if (!getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)) {
    return Promise.resolve(window.CKEDITOR.replace(id, { ...config, on: events }));
  }

  return ClassicEditor.create(typeof id === 'string' ? document.getElementById(id)! : id, {
    ...config,
    translations: [TRANSLATIONS[getLanguage().split('-')[0] as keyof typeof TRANSLATIONS]],
  }).then((instance) => configureEditor(instance, events));
};

declare module '@ckeditor/ckeditor5-core' {
  interface EditorConfig {
    filebrowserImageUploadUrl?: string;
    filebrowserImageBrowseUrl?: string;
    useCDN?: boolean;
  }
}

export const replaceAdvanced = (
  id: string | HTMLElement,
  config: EditorConfig,
  events?: Record<string, (props?: any) => void>,
) => {
  const withImages = !!config.filebrowserImageUploadUrl && !!config.filebrowserImageBrowseUrl;

  if (!getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)) {
    return Promise.resolve(
      window.CKEDITOR.replace(id, {
        allowedContent: true,
        extraAllowedContent: '*{*}',
        autoGrow_onStartup: true,
        autoGrow_maxHeight: 600,
        toolbar_Full: [
          ['Source'],
          ['Bold', 'Italic', 'Underline'],
          ['NumberedList', 'BulletedList', 'Outdent', 'Indent'],
          withImages ? ['Image', 'Link'] : ['Link'],
          ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'],
          ['TextColor', 'Format', 'PageBreak'],
          ['Table'],
        ],
        ...(get(FeatureFlag.CKEditorUploadResponseJson) ? {} : { filebrowserUploadMethod: 'form' }),
        filebrowserImageWindowWidth: '960',
        filebrowserImageWindowHeight: '480',
        format_tags: 'p;h4;h3;h2;h1',
        image_previewText: ' ',
        ...config,
        on: events,
      }),
    );
  }

  const clone = cloneDeep(advancedConfig);
  if (!withImages) {
    clone.toolbar.items.splice(9, 1); // Remove ckfinder
  }

  return ClassicEditor.create(typeof id === 'string' ? document.getElementById(id)! : id, {
    ...clone,
    ...config,
    translations: [TRANSLATIONS[getLanguage().split('-')[0] as keyof typeof TRANSLATIONS]],
  }).then((instance) => configureEditor(instance, events));
};

export const sync = () => {
  if (!getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)) {
    for (const instanceName in window.CKEDITOR.instances) {
      window.CKEDITOR.instances[instanceName].updateElement();
      window.CKEDITOR.instances[instanceName].resetDirty();
    }
    return;
  }

  for (const key in window.ckEditorInstances) {
    window.ckEditorInstances[key].updateSourceElement();
  }
};

export const destroy = (id: string) => {
  if (getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)) {
    return window.ckEditorInstances[id].destroy();
  }

  const editor = window.CKEDITOR.instances[id];

  if (editor) {
    editor.destroy(true);
  }

  return Promise.resolve();
};

export const setDataForInstance = (id: string, data: string) => {
  const editor = getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)
    ? window.ckEditorInstances[id]
    : window.CKEDITOR.instances[id];

  editor.setData(data);
};

export const getDataForInstance = (id: string) => {
  const editor = getAccountFeatureFlag(AccountFeatureFlag.UseCkeditor5)
    ? window.ckEditorInstances[id]
    : window.CKEDITOR.instances[id];

  return editor.getData();
};
